import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  CONTENT_FIELDS,
  toContentItem,
  type ContentItem,
  type ContentKind,
} from "@/lib/insights";

interface Options {
  kind?: ContentKind | "all";
  limit?: number;
}

/** Public hook: published content items, optionally filtered by kind. */
export function useContentItems({ kind = "all", limit }: Options = {}) {
  return useQuery({
    queryKey: ["content-items", kind, limit ?? null],
    queryFn: async (): Promise<ContentItem[]> => {
      let q = supabase
        .from("insights_public")
        .select(CONTENT_FIELDS)
        .eq("status", "published");
      if (kind !== "all") q = q.eq("kind", kind);
      // Unlisted whitepapers (listed=false) are hidden from every public
      // listing surface; listed whitepapers appear like normal insights.
      q = q.or("kind.neq.whitepaper,listed.eq.true");
      q = q
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });
      if (limit) q = q.limit(limit);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((r) => toContentItem(r as Record<string, unknown>));
    },
  });
}

/** Public hook: a single published item by slug + kind. */
export function useContentItem(kind: ContentKind | undefined, slug: string | undefined) {
  return useQuery({
    queryKey: ["content-item", kind, slug],
    enabled: Boolean(kind && slug),
    queryFn: async (): Promise<ContentItem | null> => {
      const { data, error } = await supabase
        .from("insights_public")
        .select(CONTENT_FIELDS)
        .eq("slug", slug!)
        .eq("kind", kind!)
        .eq("status", "published")
        .maybeSingle();
      if (error) throw error;
      return data ? toContentItem(data as Record<string, unknown>) : null;
    },
  });
}

/** Public hook: lookup an item by slug only (used by legacy /insights/article/:slug). */
export function useContentItemBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["content-item-by-slug", slug],
    enabled: Boolean(slug),
    queryFn: async (): Promise<ContentItem | null> => {
      const { data, error } = await supabase
        .from("insights_public")
        .select(CONTENT_FIELDS)
        .eq("slug", slug!)
        .eq("status", "published")
        .maybeSingle();
      if (error) throw error;
      return data ? toContentItem(data as Record<string, unknown>) : null;
    },
  });
}

/** Public hook: items by an explicit list of ids (for related content). */
export function useContentItemsByIds(ids: string[]) {
  return useQuery({
    queryKey: ["content-items-by-ids", [...ids].sort().join(",")],
    enabled: ids.length > 0,
    queryFn: async (): Promise<ContentItem[]> => {
      const { data, error } = await supabase
        .from("insights_public")
        .select(CONTENT_FIELDS)
        .in("id", ids)
        .eq("status", "published")
        // Unlisted whitepapers (listed=false) hidden from related surfaces;
        // listed whitepapers appear like normal insights.
        .or("kind.neq.whitepaper,listed.eq.true");
      if (error) throw error;
      return (data ?? []).map((r) => toContentItem(r as Record<string, unknown>));
    },
  });
}
